/**
 * ホスティングの画面の「独自ドメイン」（play.mygame.com・2026-09-27 creator/custom-domains）。
 * 一覧（状態）・追加（DNS に置く行をコピー）・今すぐ確認・削除（ホスト名を打たせる確認つき）。
 * サーバー: GET / POST …/environments/production/hosting/domains、GET / DELETE …/hosting/domains/:hostname
 * （apps/server/src/modules/hosting/domainRoutes.ts・README の「独自ドメイン」）。
 *
 * 決めたこと:
 *   - production だけ（サーバーの決まり）。development を見ているときは案内だけを出し、API を呼ばない（一覧は空で返るだけなので）
 *   - features.customDomains が OFF なら FeatureHint（ON にするコマンド・概要の「機能」への道）。一覧・確認・削除は OFF でも使える
 *     （サーバーが通す = 消さない限り原価が続くので、片付けられることが大事）ので、OFF でも一覧は出す
 *   - DNS を置くのはクリエイター本人（Kumo は他人の DNS の鍵を持たない）。だから「コピーして貼る」を一番分かりやすくする（CodeBlock のコピー）
 *   - 削除は ConfirmDialog + ホスト名の入力（その URL のプレイヤー・X に貼ったリンクがすぐ切れ、名前を他の人が取れるようになる）
 *   - 料金は数字を出さない（Money の hostname-meter が入るまで。「日割りで原価・前払いから」だけ）
 *   - 2026-09-27 Core の統合⑰（持ち主の証明・SEC-R3-07）: 置くのは CNAME と TXT（`_kumo-verify.<host>`）の 2 行。
 *     状態は status（配信）と verified（持ち主の証明）の 2 つで見せる。DNS の行は active の後も出す（折りたたみ）:
 *     TXT は毎日の確かめ直しに使うので消してはいけない = 「いま何が置いてあるべきか」を後からも見られるように。
 *     errors（サーバーの英文 = 次にやること）は状態に関係なく出す（active でも TXT が消えたら理由がここに出る）
 *   - 2026-10-06 compute/domain-paths: 1 つのドメインの下にパスごとに別のアプリ（mygame.com/race）。足す欄に「パス（任意）」・
 *     行にこのアプリの URL と同じドメインの他のアプリのパス・足した後の warnings（サーバーの英文のまま。ja は見出しだけ訳す）。
 *     外すのはこのアプリの割り当てだけ（他のアプリが残ればドメインは残る = 確認の文を変える）。
 *     新しいエラー（path_taken・domain_already_used・app_needs_domain_root）は errorText がサーバーの英文をそのまま出す
 *     （ホスト名・パスが入った具体的な文 = 訳の文に置き換えると何が重なったかが消える）
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, envPath, type EnvName } from '../api';
import { CLI } from '../cliCommand';
import {
  addTarget,
  appUrl,
  dnsText,
  domainCostMessage,
  isApex,
  parentVerifyName,
  removeKeepsDomain,
  sameHostname,
  targetLabel,
  type DomainPricing,
  type HostingDomain,
  type MountWarning
} from '../hostingDomains';
import { useResource } from '../hooks';
import { errorText, formatDateTime, useT, type MessageKey } from '../i18n';
import { FeatureHint } from './FeatureHint';
import { CodeBlock, ConfirmDialog, Spinner } from './ui';

const STATUS_PILL: Record<HostingDomain['status'], string> = { pending: 'pill-warn', active: 'pill-ok', failed: 'pill-danger' };

/**
 * onChanged: 足した・確かめた・消した後に呼ぶ（HostingPage の公開 URL を読み直す。active な独自ドメインがあると公開 URL がそちらになるため）。
 * サーバー側にも 60 秒の控えがある（hosting/domains.ts の envCache）ので、他の isolate では少し遅れて変わりうる
 */
export function HostingDomains({ projectId, env, onChanged }: { projectId: string; env: EnvName; onChanged?: () => void }) {
  const { t } = useT();
  if (env !== 'production') {
    return (
      <section className="card domains-card">
        <h2 className="card-title">{t('domains.title')}</h2>
        <p className="small muted">{t('domains.devNote')}</p>
      </section>
    );
  }
  return <ProductionDomains projectId={projectId} onChanged={onChanged} />;
}

/**
 * DNS の行の下の注記: TXT は消さない（毎日の確かめ直し）・親のドメインに 1 つで下の名前が全部通る・apex の CNAME。
 * 状態は status と verified で見る（DNS の反映待ちの間は pending のまま・TXT が見つかると verified）
 */
function DnsNotes({ hostname }: { hostname: string }) {
  const { t } = useT();
  const parent = parentVerifyName(hostname);
  return (
    <>
      <p className="small muted">{t('domains.txtKeep')}</p>
      {parent && <p className="small muted">{t('domains.parentTxt', { name: parent })}</p>}
      {isApex(hostname) && <p className="small muted">{t('domains.apexNote')}</p>}
    </>
  );
}

function ProductionDomains({ projectId, onChanged }: { projectId: string; onChanged?: () => void }) {
  const base = `${envPath(projectId, 'production')}/hosting/domains`;
  const r = useResource<{ domains: HostingDomain[]; pricing?: DomainPricing | null }>(base);
  const { t, tr } = useT();
  const [input, setInput] = useState('');
  const [pathInput, setPathInput] = useState('');
  // 足した直後の warnings（POST の応答だけにある = 一覧を読み直すと消えるので別に持つ）。次の操作・プロジェクトの切り替えで消す
  const [warnings, setWarnings] = useState<{ target: string; list: MountWarning[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  // 通知はキーと値だけ持ち、表示時に翻訳する（言語を切り替えても今の言語で出る。HostingPage と同じ）
  const [notice, setNotice] = useState<{ key: MessageKey; vars: Record<string, string> } | null>(null);
  // 外す行そのもの（ホスト名だけでなくパス・他のアプリのパスで確認の文を変える）
  const [removing, setRemoving] = useState<HostingDomain | null>(null);
  const [typed, setTyped] = useState('');
  const [removeErr, setRemoveErr] = useState<ApiError | null>(null);
  useEffect(() => {
    setNotice(null);
    setErr(null);
    setWarnings(null);
  }, [projectId]);

  const statusText = (s: HostingDomain['status']) => t(`domains.status.${s}` as MessageKey);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const { hostname, path } = addTarget(input, pathInput);
    if (!hostname) return;
    setBusy('add');
    setErr(null);
    setNotice(null);
    setWarnings(null);
    try {
      const d = await api<HostingDomain>(base, { method: 'POST', body: { hostname, path } });
      const target = targetLabel(d.hostname, d.path ?? path);
      setInput('');
      setPathInput('');
      // 同じアカウントのアプリが既に持つドメインなら DNS なしでその場で active = 「DNS を置いて」は言わない
      setNotice(
        d.status === 'active'
          ? { key: 'domains.addedLive', vars: { hostname: target, url: appUrl(d) } }
          : { key: 'domains.added', vars: { hostname: target } }
      );
      if (d.warnings?.length) setWarnings({ target, list: d.warnings });
      r.reload();
      onChanged?.();
    } catch (e2) {
      setErr(e2 as ApiError);
    } finally {
      setBusy(null);
    }
  };

  const check = async (hostname: string) => {
    setBusy(hostname);
    setErr(null);
    try {
      const d = await api<HostingDomain>(`${base}/${encodeURIComponent(hostname)}`);
      setNotice({ key: 'domains.checked', vars: { hostname: targetLabel(d.hostname, d.path), status: d.status } });
      r.reload();
      onChanged?.();
    } catch (e2) {
      setErr(e2 as ApiError);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy('remove');
    setRemoveErr(null);
    try {
      // DELETE はホスト名だけ（1 つのアプリは同じドメインに 1 回だけ = ホスト名で割り当てが決まる）
      await api(`${base}/${encodeURIComponent(removing.hostname)}`, { method: 'DELETE' });
      setNotice({ key: 'domains.removed', vars: { hostname: targetLabel(removing.hostname, removing.path) } });
      setWarnings(null);
      setRemoving(null);
      setTyped('');
      r.reload();
      onChanged?.();
    } catch (e2) {
      setRemoveErr(e2 as ApiError);
    } finally {
      setBusy(null);
    }
  };

  const domains = r.data?.domains ?? [];
  return (
    <section className="card domains-card">
      <h2 className="card-title">{t('domains.title')}</h2>
      <p className="small muted">{t('domains.lead')}</p>
      <FeatureHint projectId={projectId} env="production" need={['customDomains']} />

      {notice && (
        <div className="alert alert-ok" role="status">
          {/* 状態（pending 等）は表示時に今の言語の言葉へ */}
          {t(notice.key, notice.vars.status ? { ...notice.vars, status: statusText(notice.vars.status as HostingDomain['status']) } : notice.vars)}
        </div>
      )}
      {warnings && (
        <div className="alert alert-warn" role="status">
          {/* 見出しだけ訳す。各行はサーバーの英文（ファイル名・行・参照がそのまま入った具体的な文）のまま */}
          <p>{t('domains.warningsTitle', { hostname: warnings.target })}</p>
          <ul className="small domain-errors">
            {warnings.list.map((w, i) => (
              <li key={`${w.code}-${i}`}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}
      {r.loading && !r.data && <Spinner />}
      {r.error && (
        <p className="alert alert-error" role="alert">
          {errorText(r.error)}
        </p>
      )}
      {r.data && domains.length === 0 && <p className="muted">{t('domains.none')}</p>}

      {domains.length > 0 && (
        <ul className="domain-list">
          {domains.map((d) => (
            <li key={d.hostname} className="domain-row">
              <div className="domain-head">
                <strong className="mono">{targetLabel(d.hostname, d.path)}</strong>
                <span className={`pill ${STATUS_PILL[d.status] ?? ''}`}>{statusText(d.status)}</span>
                {/* 持ち主の証明（TXT）。古い応答で欄が無いときは出さない */}
                {d.verified !== undefined && (
                  <span className={`pill ${d.verified ? 'pill-ok' : 'pill-warn'}`}>{t(d.verified ? 'domains.verified' : 'domains.unverified')}</span>
                )}
                <span className="domain-actions">
                  {d.status === 'active' ? (
                    <a className="btn btn-ghost btn-sm" href={appUrl(d)} target="_blank" rel="noreferrer noopener">
                      {t('domains.open')} ↗
                    </a>
                  ) : (
                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy !== null} onClick={() => void check(d.hostname)}>
                      {busy === d.hostname ? t('common.working') : t('domains.check')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busy !== null}
                    onClick={() => {
                      setRemoving(d);
                      setTyped('');
                      setRemoveErr(null);
                    }}
                  >
                    {t('domains.remove')}
                  </button>
                </span>
              </div>
              {/* パスの割り当てがある応答だけ（古い応答 = ドメインの根元だけ = 今までどおり出さない） */}
              {d.path !== undefined && <p className="small muted domain-meta mono">{t('domains.appUrl', { url: appUrl(d) })}</p>}
              {!!d.otherPaths?.length && <p className="small muted domain-meta">{t('domains.otherPaths', { paths: d.otherPaths.join(', ') })}</p>}
              {d.verifiedAt && <p className="small muted domain-meta">{t('domains.verifiedAt', { at: formatDateTime(d.verifiedAt) })}</p>}
              {d.errors.length > 0 && (
                <ul className="small domain-errors">
                  {d.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
              {d.dns.length > 0 &&
                (d.status !== 'active' ? (
                  <div className="domain-dns">
                    <p className="small">{t('domains.dnsLead')}</p>
                    <CodeBlock code={dnsText(d.dns)} label={t('domains.dnsLabel')} />
                    <DnsNotes hostname={d.hostname} />
                  </div>
                ) : (
                  // 公開中: 置いたままにしておく行（特に TXT）を後からも見られるように折りたたみで残す
                  <details className="domain-dns">
                    <summary className="small">{t('domains.dnsKeep')}</summary>
                    <CodeBlock code={dnsText(d.dns)} label={t('domains.dnsLabel')} />
                    <DnsNotes hostname={d.hostname} />
                  </details>
                ))}
            </li>
          ))}
        </ul>
      )}

      <form className="form-inline domain-add" onSubmit={(e) => void add(e)}>
        <label className="field field-grow">
          <span>{t('domains.hostname')}</span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('domains.placeholder')}
            maxLength={260}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </label>
        <label className="field">
          <span>{t('domains.path')}</span>
          <input
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            placeholder={t('domains.pathPlaceholder')}
            maxLength={200}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </label>
        <button className="btn btn-primary" disabled={busy !== null || !addTarget(input, pathInput).hostname}>
          {busy === 'add' ? t('common.working') : t('domains.add')}
        </button>
      </form>
      {/* どこに出るかを先に見せる（ドメインの欄に mygame.com/race と書いた場合もここで分かる） */}
      {addTarget(input, pathInput).hostname && (
        <p className="small muted mono">{t('domains.willShow', { url: appUrl(addTarget(input, pathInput)) })}</p>
      )}
      <p className="small muted">{t('domains.pathHint')}</p>
      {err && (
        <p className="alert alert-error" role="alert">
          {errorText(err)}
        </p>
      )}
      <p className="small muted">{(() => {
        const m = domainCostMessage(r.data?.pricing);
        return t(m.key, m.vars);
      })()}</p>
      <p className="small muted">{tr('domains.cli', { cmd: <code>{`${CLI} hosting domains add play.mygame.com`}</code> })}</p>

      <ConfirmDialog
        open={!!removing}
        title={t('domains.removeTitle', { hostname: removing ? targetLabel(removing.hostname, removing.path) : '' })}
        confirmLabel={t(removing && removeKeepsDomain(removing) ? 'domains.removeLabelPath' : 'domains.removeLabel')}
        tone="danger"
        busy={busy === 'remove'}
        confirmDisabled={!removing || !sameHostname(typed, removing.hostname)}
        onClose={() => {
          setRemoving(null);
          setTyped('');
          setRemoveErr(null);
        }}
        onConfirm={() => void remove()}
      >
        {/* 他のアプリが残る = ドメインは残る（このアプリの割り当てだけ外れる）。最後の 1 つ = ドメインごと消え、他の人が登録できるようになる */}
        {removing && removeKeepsDomain(removing) ? (
          <p>{t('domains.removeBodyPath', { url: appUrl(removing), paths: (removing.otherPaths ?? []).join(', ') })}</p>
        ) : (
          <p>{t('domains.removeBody', { hostname: removing ? targetLabel(removing.hostname, removing.path) : '' })}</p>
        )}
        <label className="field">
          <span>{t('domains.removeType', { hostname: removing?.hostname ?? '' })}</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        </label>
        {removeErr && (
          <p className="alert alert-error" role="alert">
            {errorText(removeErr)}
          </p>
        )}
      </ConfirmDialog>
    </section>
  );
}
